
import React, { useRef, useEffect, useState } from 'react';
import { polygon as turfPolygon } from '@turf/turf';
import bboxClip from '@turf/bbox-clip';
import area from '@turf/area';

const SanFranciscoParkingMap = () => {
  // Refs and state variables.
  const mapRef = useRef(null);
  const mapInstance = useRef(null);
  const userMarkerRef = useRef(null);

  // State for backend data and UI.
  const [mergedIsochrones, setMergedIsochrones] = useState(null);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState({ hours: 12, minutes: 0 });
  const [coordinates, setCoordinates] = useState(null);
  const [address, setAddress] = useState('');
  const [parkingLots, setParkingLots] = useState([]);
  const [isochronePercentages, setIsochronePercentages] = useState({
    percentage5: 0,
    percentage10: 0,
    percentage15: 0,
    percentage20: 0,
  });
  const [infoMessage, setInfoMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // Host configuration for backend API calls.
  const hostPath = process.env.REACT_APP_API_BASE_URL;
  const hostHeader = process.env.REACT_APP_CUSTOM_HEADER;

  // Keys: the Azure Maps key is used for reverse geocoding; Google Maps key is used to load the map.
  const azureMapsKey = process.env.REACT_APP_AZURE_MAPS_KEY;
  const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
  // Map type state for view selection.
//   const [mapType, setMapType] = useState('roadmap');
  const [mapType, setMapType] = useState('hybrid');


  // --- Load Google Maps Script ---
  useEffect(() => {
    if (!window.google) {
      const script = document.createElement('script');
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`;
      script.async = true;
      script.defer = true;
      script.onload = initMap;
      document.head.appendChild(script);
    } else {
      initMap();
    }
  }, [googleMapsApiKey]);

  // --------------------------------------------------------------------------
  // ADD THIS useEffect TO REMOVE THE POP-UP BY TEXT CONTENT
  // --------------------------------------------------------------------------
  useEffect(() => {
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType === 1) {
            // Check if this newly added node or any of its children has the pop-up text
            const text = node.textContent || '';
            if (
              text.includes("This page can't load Google Maps correctly") ||
              text.includes("Do you own this website?")
            ) {
              node.remove();
            }
          }
        }
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);
  // --------------------------------------------------------------------------

  // Update map type when user selects a different view.
  useEffect(() => {
    if (mapInstance.current) {
      mapInstance.current.setMapTypeId(mapType);
    }
  }, [mapType]);

  // --- Initialize Google Map ---
  const initMap = () => {
    if (!mapRef.current) return;
    mapInstance.current = new window.google.maps.Map(mapRef.current, {
      center: { lat: 37.785834, lng: -122.406417 },
      zoom: 14,
      mapTypeId: mapType,
      // Adding mapId to avoid development watermarks.
      fullscreenControl: false,
      mapId: 'da37f3254c6a6d1c',
      tilt: 10
    });

    // Add a click listener to trigger reverse geocoding and backend calls.
    mapInstance.current.addListener('click', handleMapClick);
    // Update isochrone coverage when the map becomes idle.
    mapInstance.current.addListener('idle', updateIsochroneCoverage);
  };

  // --- Azure Maps Reverse Geocoding ---
  const getAddressFromCoordinates = async (latitude, longitude) => {
    const url = `https://atlas.microsoft.com/search/address/reverse/json?api-version=1.0&query=${latitude},${longitude}&subscription-key=${azureMapsKey}`.replace(
      /\s+/g,
      ''
    );
    try {
      const response = await fetch(url);
      const data = await response.json();
      return data?.addresses?.[0]?.address?.freeformAddress || 'Unknown address';
    } catch (error) {
      console.error('Reverse Geocoding Error:', error);
      return 'Address lookup failed';
    }
  };

  // --- Helper: Convert Time Object to HH:MM ---
  const convertTo24HourFormat = (timeObj) => {
    const { hours, minutes } = timeObj;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}`;
  };

  // --- Call Backend API ---
  const sendDataToBackend = async (addr) => {
    try {
      const formattedDate = selectedDate.toISOString().split('T')[0];
      const formattedTime = convertTo24HourFormat(selectedTime);
      const response = await fetch(
        `${hostPath}/api/GetParkingIsochrones?address=${encodeURIComponent(
          addr
        )}&rangeKm=10&date=${formattedDate}&time=${formattedTime}`,
        { headers: { 'x-functions-key': hostHeader } }
      );
      if (response.status === 404) {
        setInfoMessage('No parking lots found in the nearby area.');
        return;
      }
      if (!response.ok) throw new Error(`Server error: ${response.status}`);
      const data = await response.json();
      let lots = data.parkingLots || [];
      if (!Array.isArray(lots)) {
        lots = [];
      }
      // Save merged isochrone polygons and parking lot data.
      setMergedIsochrones(data.mergedIsochrones);
      setParkingLots(lots);
      setInfoMessage('');
    } catch (error) {
      console.error('Fetch error:', error);
      setInfoMessage('Error fetching parking data. Please try again.');
    }
  };

  // --- Map Click Handler ---
  const handleMapClick = async (event) => {
    if (isLoading) return;
    setIsLoading(true);
    try {
      const lat = event.latLng.lat();
      const lng = event.latLng.lng();
      setCoordinates([lng, lat]);
      const locationAddress = await getAddressFromCoordinates(lat, lng);
      setAddress(locationAddress);
      if (!userMarkerRef.current) {
        userMarkerRef.current = new window.google.maps.Marker({
          position: { lat, lng },
          map: mapInstance.current,
          draggable: true,
          label: '📍',
        });
        userMarkerRef.current.addListener('dragend', async (e) => {
          const newLat = e.latLng.lat();
          const newLng = e.latLng.lng();
          setCoordinates([newLng, newLat]);
          const newAddr = await getAddressFromCoordinates(newLat, newLng);
          setAddress(newAddr);
          await sendDataToBackend(newAddr);
        });
      } else {
        userMarkerRef.current.setPosition({ lat, lng });
      }
      mapInstance.current.setCenter({ lat, lng });
      await sendDataToBackend(locationAddress);
    } catch (error) {
      console.error('Error in handleMapClick:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // --- Isochrone Rendering ---
  useEffect(() => {
    if (!mapInstance.current || !mergedIsochrones) return;

    // Clear previously drawn isochrone polygons.
    if (window.__isochronePolygons) {
      window.__isochronePolygons.forEach((poly) => poly.setMap(null));
    }
    window.__isochronePolygons = [];

    const addIsochroneLayer = (duration, color) => {
      const polygonsArray = mergedIsochrones[duration];
      if (!polygonsArray || polygonsArray.length === 0) return;
      polygonsArray.forEach((poly) => {
        const path = poly.map((pt) => ({
          lat: pt.Lat || pt.lat,
          lng: pt.Lng || pt.lng,
        }));
        if (path.length > 0) {
          const first = path[0];
          const last = path[path.length - 1];
          if (first.lat !== last.lat || first.lng !== last.lng) {
            path.push(first);
          }
        }
        const polygon = new window.google.maps.Polygon({
          paths: path,
          strokeColor: color,
          strokeOpacity: 0.8,
          strokeWeight: 2,
          fillColor: color,
          fillOpacity: 0.5,
          map: mapInstance.current,
        });
        window.__isochronePolygons.push(polygon);
      });
    };

    addIsochroneLayer('20', 'rgba(219, 23, 19, 0.5)');
    addIsochroneLayer('15', 'rgba(247, 139, 7, 0.8)');
    addIsochroneLayer('10', 'rgba(251, 198, 47, 0.8)');
    addIsochroneLayer('5', 'rgba(76, 175, 80, 0.9)');
  }, [mergedIsochrones]);

  // --- Isochrone Coverage Calculation ---
  const updateIsochroneCoverage = () => {
    if (!mapInstance.current || !mergedIsochrones) return;
    const bounds = mapInstance.current.getBounds();
    if (!bounds) return;
    const ne = bounds.getNorthEast();
    const sw = bounds.getSouthWest();
    const bbox = [sw.lng(), sw.lat(), ne.lng(), ne.lat()];
    let visibleAreas = { '5': 0, '10': 0, '15': 0, '20': 0 };

    Object.keys(mergedIsochrones).forEach((duration) => {
      mergedIsochrones[duration].forEach((poly) => {
        try {
          const coords = poly.map((pt) => [pt.Lng || pt.lng, pt.Lat || pt.lat]);
          if (coords.length > 0) {
            const first = coords[0];
            const last = coords[coords.length - 1];
            if (first[0] !== last[0] || first[1] !== last[1]) {
              coords.push(first);
            }
          }
          const turfPoly = turfPolygon([coords]);
          const clipped = bboxClip(turfPoly, bbox);
          visibleAreas[duration] += area(clipped);
        } catch (e) {
          console.error(`Error clipping ${duration}-min polygon:`, e);
        }
      });
    });

    const area5 = visibleAreas['5'];
    const area10Excl = Math.max(visibleAreas['10'] - visibleAreas['5'], 0);
    const area15Excl = Math.max(visibleAreas['15'] - visibleAreas['10'], 0);
    const area20Excl = Math.max(visibleAreas['20'] - visibleAreas['15'], 0);
    const totalVisibleArea = area5 + area10Excl + area15Excl + area20Excl;
    if (totalVisibleArea > 0) {
      setIsochronePercentages({
        percentage5: ((area5 / totalVisibleArea) * 100).toFixed(2),
        percentage10: ((area10Excl / totalVisibleArea) * 100).toFixed(2),
        percentage15: ((area15Excl / totalVisibleArea) * 100).toFixed(2),
        percentage20: ((area20Excl / totalVisibleArea) * 100).toFixed(2),
      });
    } else {
      setIsochronePercentages({
        percentage5: 0,
        percentage10: 0,
        percentage15: 0,
        percentage20: 0,
      });
    }
    console.log('Visible areas:', visibleAreas, 'Total area:', totalVisibleArea);
  };

  // Update isochrone coverage when the map is idle.
  useEffect(() => {
    if (!mapInstance.current) return;
    mapInstance.current.addListener('idle', updateIsochroneCoverage);
  }, [mergedIsochrones]);

  const handleRefreshClick = () => {
    window.location.reload();
  };

  return (
    <div style={{ width: '100%', height: '100vh', position: 'relative' }}>
      {/* Google Map Container */}
      <div ref={mapRef} style={{ width: '100%', height: '100%' }} />

      {/* Icon-Style Map View Selector */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          right: 20,
          display: 'flex',
          gap: '8px',
          background: 'rgba(255,255,255,0.9)',
          padding: '6px 8px',
          borderRadius: '8px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
        }}
      >
        <button
          onClick={() => setMapType('roadmap')}
          style={{
            background: mapType === 'roadmap' ? '#0078D4' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
          }}
          title="Roadmap"
        >
          🗺️
        </button>
        <button
          onClick={() => setMapType('satellite')}
          style={{
            background: mapType === 'satellite' ? '#0078D4' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
          }}
          title="Satellite"
        >
          🛰️
        </button>
        <button
          onClick={() => setMapType('hybrid')}
          style={{
            background: mapType === 'hybrid' ? '#0078D4' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
          }}
          title="Hybrid"
        >
          🌐
        </button>
        <button
          onClick={() => setMapType('terrain')}
          style={{
            background: mapType === 'terrain' ? '#0078D4' : 'transparent',
            border: 'none',
            cursor: 'pointer',
            fontSize: '18px',
          }}
          title="Terrain"
        >
          ⛰️
        </button>
      </div>

      {/* Loading Overlay */}
      {isLoading && (
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255,255,255,0.7)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2000,
          }}
        >
          <div
            style={{
              padding: 20,
              background: 'white',
              borderRadius: 8,
              boxShadow: '0 2px 8px rgba(0,0,0,0.3)',
            }}
          >
            Loading isochrones...
          </div>
        </div>
      )}

      {/* Control Panel (Date and Time selection) */}
      <div
        style={{
          position: 'absolute',
          top: 20,
          left: 20,
          background: 'white',
          padding: 15,
          borderRadius: 8,
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 280,
        }}
      >
        <label style={{ display: 'block', marginBottom: 5 }}>
          Select Date:
          <input
            type="date"
            value={selectedDate.toISOString().split('T')[0]}
            onChange={(e) => setSelectedDate(new Date(e.target.value))}
            style={{ width: '100%', padding: 8, marginTop: 5 }}
          />
        </label>
        <label style={{ display: 'block', margin: '15px 0 5px' }}>
          Select Time:
          <input
            type="time"
            value={`${selectedTime.hours
              .toString()
              .padStart(2, '0')}:${selectedTime.minutes
              .toString()
              .padStart(2, '0')}`}
            onChange={(e) => {
              const [hh, mm] = e.target.value.split(':');
              setSelectedTime({ hours: Number(hh), minutes: Number(mm) });
            }}
            style={{ width: '100%', padding: 8, marginTop: 5 }}
          />
        </label>
      </div>

      {/* Info Message Box */}
      {infoMessage && (
        <div
          style={{
            position: 'absolute',
            top: 20,
            left: '50%',
            transform: 'translateX(-50%)',
            background: '#ffcccc',
            padding: '10px 20px',
            borderRadius: '5px',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
            zIndex: 1100,
          }}
        >
          {infoMessage}
        </div>
      )}

      {/* Isochrone Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 140,
          left: 20,
          background: 'white',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 220,
        }}
      >
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
          Drive Time Isochrones
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(76,175,80,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>5 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(251,198,47,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>10 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(247,139,7,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>15 minutes</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(219,23,19,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>20 minutes</span>
        </div>
      </div>

      {/* Isochrone Percentage Legend */}
      <div
        style={{
          position: 'absolute',
          bottom: 280,
          left: 20,
          background: 'white',
          padding: '10px',
          borderRadius: '5px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          zIndex: 1000,
          width: 220,
        }}
      >
        <div style={{ marginBottom: '5px', fontWeight: 'bold' }}>
          Isochrone Coverage (%)
        </div>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(76,175,80,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>5 minutes: {isochronePercentages.percentage5}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(251,198,47,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>10 minutes: {isochronePercentages.percentage10}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(247,139,7,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>15 minutes: {isochronePercentages.percentage15}%</span>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', marginTop: '5px' }}>
          <div
            style={{
              width: '15px',
              height: '15px',
              background: 'rgba(219,23,19,1)',
              marginRight: '5px',
            }}
          ></div>
          <span>20 minutes: {isochronePercentages.percentage20}%</span>
        </div>
      </div>

      {/* Refresh Button */}
      <div
        style={{
          position: 'absolute',
          bottom: 20,
          left: '50%',
          transform: 'translateX(-50%)',
          zIndex: 1000,
        }}
      >
        <button
          onClick={handleRefreshClick}
          style={{
            backgroundColor: '#0078D4',
            color: 'white',
            padding: '10px 20px',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer',
            fontWeight: 'bold',
          }}
        >
          Reset
        </button>
      </div>
    </div>
  );
};

export default SanFranciscoParkingMap;
